import React, { useState } from "react";
import PropTypes from "prop-types";
import {
  Alert,
  Box,
  Button,
  Chip,
  Grid,
  LinearProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import { plateDate, plateList, plateState } from "../../model/plateModel";
const colors = {
  success: ["#e4f3e9", "#217346"],
  warning: ["#fff1d5", "#885515"],
  error: ["#fde5e2", "#b42318"],
  info: ["#eef3f8", "#526579"],
};
function Status({ state }) {
  return (
    <Chip
      size="small"
      label={state.label}
      sx={{
        bgcolor: colors[state.severity][0],
        color: colors[state.severity][1],
        fontWeight: 600,
        maxWidth: "100%",
        height: "auto",
        "& .MuiChip-label": { whiteSpace: "normal", py: 0.6 },
      }}
    />
  );
}
Status.propTypes = { state: PropTypes.object.isRequired };
function StateCard({ title, state }) {
  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2.5,
        height: "100%",
        borderRadius: 2,
        borderTop: `4px solid ${colors[state.severity][1]}`,
      }}
    >
      <Typography sx={{ fontSize: ".85rem", fontWeight: 700, color: "#183b50", mb: 2 }}>
        {title}
      </Typography>
      <Status state={state} />
      <Typography sx={{ fontSize: ".8rem", color: "#526579", mt: 1.5 }}>
        {state.description}
      </Typography>
      <Typography sx={{ fontSize: ".72rem", color: "#526579", mt: 1 }}>
        Código: {state.code ?? "Sin información"}
      </Typography>
    </Paper>
  );
}
StateCard.propTypes = { title: PropTypes.string.isRequired, state: PropTypes.object.isRequired };
export default function TablePlacas({ state }) {
  const result = state.resultadoPlaca;
  const [page, setPage] = useState(0),
    [size, setSize] = useState(25);
  const rows = state.historialConsultas;
  const currentPage = Math.min(page, Math.max(0, Math.ceil(rows.length / size) - 1));
  const commerceName = (id) =>
    state.comercios.find((item) => item.idcommerce === id)?.namecomercio || `Comercio ${id}`;
  return (
    <Box>
      {state.loading && (
        <Box sx={{ mb: 2 }}>
          <LinearProgress />
          <Typography sx={{ fontSize: ".8rem", color: "#526579", mt: 1 }}>
            Consultando la placa seleccionada. Puede cambiar la consulta o cancelarla con «Limpiar
            consulta».
          </Typography>
        </Box>
      )}
      {state.error && (
        <Alert
          severity="warning"
          sx={{ mb: 2 }}
          action={
            state.ultimoError?.canRetry === true ? (
              <Button onClick={state.reintentarUltimaConsulta} disabled={state.loading}>
                Reintentar
              </Button>
            ) : null
          }
        >
          {state.error}
        </Alert>
      )}
      {result && (
        <Paper sx={{ p: { xs: 2, md: 3 }, mb: 3, borderRadius: 3 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 2,
              flexWrap: "wrap",
              mb: 2,
            }}
          >
            <Box>
              <Typography sx={{ fontSize: ".75rem", color: "#526579" }}>
                Resultado de la consulta
              </Typography>
              <Typography sx={{ fontSize: "1.2rem", fontWeight: 700, color: "#183b50" }}>
                {result.plate} ·{" "}
                {result.commerce || result.comercio || commerceName(result.commerceId)}
              </Typography>
              <Typography sx={{ fontSize: ".74rem", color: "#526579", mt: 0.5 }}>
                Recibido: {plateDate(result.receivedAt)} · Colombia
              </Typography>
            </Box>
            {!result.isNotGopass && (
              <Status state={plateList(result.vehicle_list_commerce_poseidon)} />
            )}
          </Box>
          {result.isNotGopass ? (
            <Alert severity="info">
              {result.message || "El servicio informa que este vehículo no es Gopass."}
            </Alert>
          ) : (
            <>
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <StateCard
                    title="Cerberus · Acceso al comercio"
                    state={plateState(result.status, "cerberus", result.status_description)}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <StateCard
                    title="Poseidon · Vehículo"
                    state={plateState(
                      result.status_vehicle_poseidon,
                      "vehicle",
                      result.status_vehicle_description_poseidon
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <StateCard
                    title="Poseidon · Usuario"
                    state={plateState(
                      result.status_user_poseidon,
                      "user",
                      result.status_user_description_poseidon
                    )}
                  />
                </Grid>
              </Grid>
              <Typography
                sx={{ fontSize: ".85rem", fontWeight: 700, color: "#183b50", mt: 3, mb: 1.5 }}
              >
                Actualizaciones reportadas · Colombia (UTC−5)
              </Typography>
              <Grid container spacing={2}>
                {[
                  ["Última actualización Cerberus", result.last_update_list_cerberus],
                  ["Próxima actualización Cerberus", result.next_update_list_cerberus],
                  ["Actualización del vehículo", result.update_vehicle_poseidon],
                  ["Actualización del usuario", result.update_user_poseidon],
                  ["Modificación de lista", result.dateMod_lista],
                  ["Actualización lista Poseidon", result.vehicle_list_commerce_update_poseidon],
                  ["Consulta de lista", result.fecha_consulta_lista],
                  ["Inicio de lista", result.start_lista],
                  ["Fin de lista", result.fin_lista],
                ].map(([label, value]) => (
                  <Grid item xs={12} sm={6} md={4} key={label}>
                    <Box sx={{ p: 1.5, bgcolor: "#f4f7fa", borderRadius: 2 }}>
                      <Typography sx={{ fontSize: ".72rem", color: "#526579" }}>{label}</Typography>
                      <Typography
                        sx={{ fontSize: ".8rem", fontWeight: 600, color: "#183b50", mt: 0.5 }}
                      >
                        {plateDate(value)}
                      </Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </>
          )}
          <Box component="details" sx={{ mt: 2, borderTop: "1px solid #e1e8ef", pt: 2 }}>
            <Typography
              component="summary"
              sx={{ fontSize: ".8rem", color: "#526579", cursor: "pointer" }}
            >
              Datos originales del servicio
            </Typography>
            <Box
              component="pre"
              sx={{
                bgcolor: "#f4f7fa",
                p: 2,
                fontSize: ".72rem",
                maxHeight: 260,
                overflow: "auto",
              }}
            >
              {JSON.stringify(result.raw, null, 2)}
            </Box>
          </Box>
        </Paper>
      )}
      {!result && !state.loading && !state.error && (
        <Alert severity="info" sx={{ mb: 3 }}>
          Seleccione un comercio e ingrese una placa para consultar su estado.
        </Alert>
      )}
      <Paper sx={{ p: { xs: 2, md: 3 }, borderRadius: 3 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
            gap: 1,
            flexWrap: "wrap",
          }}
        >
          <Box>
            <Typography sx={{ fontSize: "1rem", fontWeight: 700, color: "#183b50" }}>
              Historial de esta sesión
            </Typography>
            <Typography sx={{ fontSize: ".75rem", color: "#526579" }}>
              Incluye consultas con resultado, sin registro y con error. Se conserva mientras esta
              vista está abierta.
            </Typography>
          </Box>
          <Chip
            label={`${rows.length} consultas`}
            size="small"
            sx={{ bgcolor: "#e8eff7", color: "#183b50" }}
          />
        </Box>
        <TableContainer sx={{ maxHeight: "55vh", border: "1px solid #e1e8ef", borderRadius: 2 }}>
          <Table stickyHeader size="small" sx={{ minWidth: 850 }}>
            <TableHead sx={{ display: "table-header-group", p: 0 }}>
              <TableRow>
                {[
                  "Placa",
                  "Comercio",
                  "Resultado de consulta",
                  "Cerberus",
                  "Vehículo Poseidon",
                  "Usuario Poseidon",
                  "Fecha · Colombia",
                ].map((label) => (
                  <TableCell
                    key={label}
                    sx={{ bgcolor: "#183b50", color: "#fff", fontWeight: 700, fontSize: ".72rem" }}
                  >
                    {label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.slice(currentPage * size, (currentPage + 1) * size).map((row) => (
                <TableRow key={row.id} hover sx={{ "&:nth-of-type(even)": { bgcolor: "#f7f9fc" } }}>
                  <TableCell sx={{ fontWeight: 700, color: "#183b50" }}>{row.plate}</TableCell>
                  <TableCell sx={{ fontSize: ".8rem" }}>
                    {row.data?.commerce || row.data?.comercio || commerceName(row.commerceId)}
                  </TableCell>
                  <TableCell>
                    <Tooltip title={row.error || row.message || "Consulta recibida"}>
                      <Box sx={{ display: "inline-flex" }}>
                        <Status
                          state={{
                            label: row.isNotGopass
                              ? "No es Gopass"
                              : row.success
                              ? "Resultado recibido"
                              : row.isTimeout
                              ? "Tiempo agotado"
                              : "Error de consulta",
                            severity: row.success
                              ? "success"
                              : row.isNotGopass
                              ? "info"
                              : "warning",
                          }}
                        />
                      </Box>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    <Status
                      state={plateState(row.data?.status, "cerberus", row.data?.status_description)}
                    />
                  </TableCell>
                  <TableCell>
                    <Status state={plateState(row.data?.status_vehicle_poseidon, "vehicle")} />
                  </TableCell>
                  <TableCell>
                    <Status state={plateState(row.data?.status_user_poseidon, "user")} />
                  </TableCell>
                  <TableCell sx={{ fontSize: ".75rem", whiteSpace: "nowrap" }}>
                    {plateDate(row.receivedAt)}
                  </TableCell>
                </TableRow>
              ))}
              {!rows.length && (
                <TableRow>
                  <TableCell colSpan={7} sx={{ textAlign: "center", p: 4, color: "#526579" }}>
                    Aún no hay consultas registradas.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={rows.length}
          rowsPerPage={size}
          page={currentPage}
          rowsPerPageOptions={[10, 25, 50, 100, 200]}
          onPageChange={(_, next) => setPage(next)}
          onRowsPerPageChange={(event) => {
            setSize(Number(event.target.value));
            setPage(0);
          }}
          labelRowsPerPage="Filas por página"
          labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`}
        />
      </Paper>
    </Box>
  );
}
TablePlacas.propTypes = { state: PropTypes.object.isRequired };
